import { ALL_GAME_INPUT_ACTIONS, type GameInputAction } from './InputAction';
import {
  DEFAULT_INPUT_ACTION_BINDINGS,
  freezeInputBinding,
  type GamepadAxisBinding,
  type InputActionBinding,
  type InputDevice,
} from './InputBindings';

const STORAGE_KEY = 'rl-web.input-bindings.v2';
const STORAGE_VERSION = 2;
const PREVIOUS_STORAGE_KEY = 'rl-web.input-bindings.v1';

export type InputBindingProfile = Readonly<Record<GameInputAction, InputActionBinding>>;

export type CapturedInput =
  | Readonly<{ kind: 'keyboard'; code: string }>
  | Readonly<{ kind: 'mouse'; button: number }>
  | Readonly<{ kind: 'gamepad-button'; button: number }>
  | Readonly<{ kind: 'gamepad-axis'; axis: number; direction: -1 | 1 }>;

export class InputBindingStore {
  #bindings: InputBindingProfile;
  #storage: Storage | null;

  constructor(storage: Storage | null | undefined = browserStorage()) {
    this.#storage = storage ?? null;
    this.#bindings = loadProfile(this.#storage);
    try {
      if (this.#storage?.getItem(PREVIOUS_STORAGE_KEY) && !this.#storage.getItem(STORAGE_KEY)) {
        if (this.#persist()) this.#storage?.removeItem(PREVIOUS_STORAGE_KEY);
      }
    } catch { this.#storage = null; }
  }

  get bindings(): InputBindingProfile {
    return this.#bindings;
  }

  setBinding(action: GameInputAction, device: InputDevice, captured: CapturedInput | null): boolean {
    const current = this.#bindings[action];
    const next: InputActionBinding = device === 'keyboard-mouse'
      ? {
          ...current,
          keyboardCodes: captured?.kind === 'keyboard' ? [captured.code] : [],
          mouseButtons: captured?.kind === 'mouse' ? [captured.button] : [],
        }
      : {
          ...current,
          gamepadButtons: captured?.kind === 'gamepad-button' ? [captured.button] : [],
          gamepadAxes: captured?.kind === 'gamepad-axis'
            ? [{ axis: captured.axis, direction: captured.direction }]
            : [],
        };
    this.#bindings = replaceAction(this.#bindings, action, freezeInputBinding(next));
    return this.#persist();
  }

  resetAction(action: GameInputAction): boolean {
    this.#bindings = replaceAction(this.#bindings, action, DEFAULT_INPUT_ACTION_BINDINGS[action]);
    return this.#persist();
  }

  resetAll(): boolean {
    this.#bindings = cloneProfile(DEFAULT_INPUT_ACTION_BINDINGS);
    return this.#persist();
  }

  #persist(): boolean {
    if (!this.#storage) return false;
    try {
      this.#storage.setItem(STORAGE_KEY, JSON.stringify({ version: STORAGE_VERSION, bindings: this.#bindings }));
      return true;
    } catch {
      this.#storage = null;
      return false;
    }
  }
}

function browserStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function loadProfile(storage: Storage | null): InputBindingProfile {
  if (!storage) return cloneProfile(DEFAULT_INPUT_ACTION_BINDINGS);
  try {
    const current = storage.getItem(STORAGE_KEY);
    const raw = current ?? storage.getItem(PREVIOUS_STORAGE_KEY);
    if (!raw) return cloneProfile(DEFAULT_INPUT_ACTION_BINDINGS);
    const parsed: unknown = JSON.parse(raw);
    if (!isObject(parsed) || parsed.version !== (current ? STORAGE_VERSION : 1) || !isObject(parsed.bindings)) {
      return cloneProfile(DEFAULT_INPUT_ACTION_BINDINGS);
    }
    const result = {} as Record<GameInputAction, InputActionBinding>;
    for (const action of ALL_GAME_INPUT_ACTIONS) {
      const persistedAction = !current && action === 'open-session-menu' ? 'exit-session' : action;
      const binding = parseBinding(parsed.bindings[persistedAction]);
      if (!current && action === 'open-session-menu' && binding?.keyboardCodes.length === 1
        && binding.keyboardCodes[0] === 'Escape' && binding.mouseButtons.length === 0
        && binding.gamepadButtons.length === 0 && binding.gamepadAxes.length === 0) {
        result[action] = DEFAULT_INPUT_ACTION_BINDINGS[action];
      } else result[action] = binding ?? DEFAULT_INPUT_ACTION_BINDINGS[action];
    }
    return Object.freeze(result);
  } catch {
    return cloneProfile(DEFAULT_INPUT_ACTION_BINDINGS);
  }
}

function parseBinding(value: unknown): InputActionBinding | null {
  if (!isObject(value)) return null;
  const keyboardCodes = stringArray(value.keyboardCodes, 8, 64);
  const mouseButtons = integerArray(value.mouseButtons, 8, 0, 8);
  const gamepadButtons = integerArray(value.gamepadButtons, 8, 0, 31);
  const gamepadAxes = axisArray(value.gamepadAxes);
  if (!keyboardCodes || !mouseButtons || !gamepadButtons || !gamepadAxes) return null;
  return freezeInputBinding({ keyboardCodes, mouseButtons, gamepadButtons, gamepadAxes });
}

function axisArray(value: unknown): readonly GamepadAxisBinding[] | null {
  if (!Array.isArray(value) || value.length > 8) return null;
  const result: GamepadAxisBinding[] = [];
  for (const item of value) {
    if (!isObject(item) || !Number.isInteger(item.axis) || (item.axis as number) < 0 || (item.axis as number) > 15
      || (item.direction !== -1 && item.direction !== 1)) return null;
    result.push({ axis: item.axis as number, direction: item.direction });
  }
  return result;
}

function stringArray(value: unknown, maximumItems: number, maximumLength: number): readonly string[] | null {
  if (!Array.isArray(value) || value.length > maximumItems) return null;
  if (!value.every((item) => typeof item === 'string' && item.length > 0 && item.length <= maximumLength)) return null;
  return value;
}

function integerArray(value: unknown, maximumItems: number, minimum: number, maximum: number): readonly number[] | null {
  if (!Array.isArray(value) || value.length > maximumItems) return null;
  if (!value.every((item) => Number.isInteger(item) && item >= minimum && item <= maximum)) return null;
  return value;
}

function cloneProfile(profile: InputBindingProfile): InputBindingProfile {
  const result = {} as Record<GameInputAction, InputActionBinding>;
  for (const action of ALL_GAME_INPUT_ACTIONS) result[action] = freezeInputBinding(profile[action]);
  return Object.freeze(result);
}

function replaceAction(profile: InputBindingProfile, action: GameInputAction, binding: InputActionBinding): InputBindingProfile {
  return Object.freeze({ ...profile, [action]: binding });
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
